import type { ProcessingHistoryRecord } from "./report-preview";

export type DashboardMetricPreset = "today" | "past-3-days" | "past-week" | "last-month" | "custom";

export interface DashboardMetricDateRange {
  startDate: string;
  endDate: string;
}

export interface DashboardMetricSummary {
  receivedApplications: number;
  processingApplications: number;
  pendingApplications: number;
  errorApplications: number;
}

function dateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function dashboardMetricDateRangeForPreset(
  preset: Exclude<DashboardMetricPreset, "custom">,
  referenceDate = new Date(),
): DashboardMetricDateRange {
  const end = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
  const start = new Date(end);

  if (preset === "past-3-days") start.setDate(start.getDate() - 2);
  if (preset === "past-week") start.setDate(start.getDate() - 6);
  if (preset === "last-month") {
    start.setFullYear(end.getFullYear(), end.getMonth() - 1, 1);
    end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
  }

  return { startDate: dateInputValue(start), endDate: dateInputValue(end) };
}

export function summarizeDashboardMetrics(
  records: ProcessingHistoryRecord[],
  openJobIds: string[],
  dateRange: DashboardMetricDateRange,
): DashboardMetricSummary {
  const visibleJobs = new Set(openJobIds.map((jobId) => jobId.toUpperCase()));
  const startTime = new Date(`${dateRange.startDate}T00:00:00`).getTime();
  const endExclusiveDate = new Date(`${dateRange.endDate}T00:00:00`);
  endExclusiveDate.setDate(endExclusiveDate.getDate() + 1);
  const endExclusive = endExclusiveDate.getTime();
  const summary: DashboardMetricSummary = {
    receivedApplications: 0,
    processingApplications: 0,
    pendingApplications: 0,
    errorApplications: 0,
  };

  records.forEach((record) => {
    if (!visibleJobs.has(record.jobId.toUpperCase())) return;
    const receivedAt = new Date(record.appliedAt).getTime();
    if (!Number.isFinite(receivedAt) || receivedAt < startTime || receivedAt >= endExclusive) return;
    summary.receivedApplications += 1;
    if (record.aiState === "Processing") summary.processingApplications += 1;
    if (record.aiState === "Pending") summary.pendingApplications += 1;
    if (record.aiState === "Failed") summary.errorApplications += 1;
  });

  return summary;
}
